import React, { useEffect, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { useStripe } from "@stripe/react-stripe-js";

import Config from "../../Config";
import { formatMoney } from "../../../utils/orders";
import { readPendingWalletOrder } from "../../../utils/walletOrder";
import { ResultScreen } from "../Feedback";
import { Icon, OnboardingIcons } from "../OnboardingLayout";

/* Illustration du design ; l'ancienne image tant que le fichier n'est pas livré. */
const ILLUSTRATION = "/assets/imgs/illustration-scooter.png";
const FALLBACK = "/assets/imgs/Delivery_men.png";

/**
 * Portefeuille rechargé — design « Pop-ups et confirmations », écran 06.
 * Stripe y renvoie après Bancontact (return_url de la recharge).
 *
 * Montant ajouté : le paiement Stripe désigné par l'URL, s'il a abouti.
 * Nouveau solde : GET /customer/wallet. L'encart n'affiche que ce qui est
 * connu. Le crédit lui-même est fait côté serveur, comme avant : cette page
 * n'écrit rien.
 *
 * Retour : là d'où la recharge est partie (localStorage « redirect », écrit
 * par la fenêtre de recharge) : le paiement, le profil, « Mes commandes »…
 *
 * Recharge lancée par le récapitulatif pour couvrir une commande (entrée
 * « walletOrder », voir utils/walletOrder) : si le paiement a abouti, on ne
 * montre pas cet écran, on repart au récapitulatif qui confirme la commande
 * et ouvre l'écran « commande confirmée ».
 *
 * Animations : aucune, comme l'écran 06 (ni bulles ni flottement).
 */
export const ToppedUp = ({ authUser, history }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const [added, setAdded] = useState(null);
  const [balance, setBalance] = useState(null);
  const [redirect] = useState(() => localStorage.getItem("redirect"));
  const [secret] = useState(() =>
    new URLSearchParams(window.location.search).get("payment_intent_client_secret")
  );
  /* Une commande attend : rien à afficher tant que Stripe n'a pas répondu. */
  const [resolving, setResolving] = useState(
    () =>
      Boolean(secret) &&
      String(redirect || "").startsWith("/checkout") &&
      Boolean(readPendingWalletOrder())
  );

  useEffect(() => {
    Axios({
      url: `${Config.apiURL}/customer/wallet`,
      method: "GET",
      headers: {
        uid: authUser?.user_uid,
        client: authUser?.user_client,
        "access-token": authUser?.user_access_token,
      },
    })
      .then((response) => {
        const amount = Number(response?.data?.wallet?.amount);
        setBalance(Number.isFinite(amount) ? amount : null);
      })
      .catch(() => setBalance(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!stripe || !secret) return;
    stripe
      .retrievePaymentIntent(secret)
      .then(({ paymentIntent }) => {
        const succeeded = paymentIntent?.status === "succeeded";

        if (succeeded && resolving) {
          localStorage.removeItem("redirect");
          history.replace(redirect, { walletOrder: true });
          return;
        }

        if (succeeded) setAdded(paymentIntent.amount / 100);
        setResolving(false);
      })
      .catch(() => {
        setAdded(null);
        setResolving(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  const fromCheckout = String(redirect || "").startsWith("/checkout");
  const backLabel = fromCheckout
    ? t("Back to payment")
    : redirect === "/profile"
    ? t("Back to profile")
    : t("Back");

  const back = () => {
    localStorage.removeItem("redirect");
    history.replace(redirect || "/home");
  };

  const facts = [
    added !== null && { label: t("Amount added"), value: `+${formatMoney(added)}`, accent: true },
    balance !== null && { label: t("New balance"), value: formatMoney(balance) },
  ].filter(Boolean);

  if (resolving) {
    return null;
  }

  return (
    <>
      {/* Sprite des pictos (#ob-check de la pastille). */}
      <OnboardingIcons />
      <ResultScreen
        tone="success"
        halo="medium"
        bubbles={false}
        float={false}
        art="small"
        illustration={ILLUSTRATION}
        fallbackIllustration={FALLBACK}
        badge={
          <>
            <Icon name="check" />
            {t("Payment received")}
          </>
        }
        title={t("Your wallet has been topped up!")}
        lede={
          fromCheckout
            ? t("Your balance now covers your order. Go back to payment to confirm it.")
            : t("Your balance is ready for your next orders.")
        }
        facts={facts}
        factsSize="large"
        wideAction
        actions={[
          {
            label: backLabel,
            onClick: back,
            primary: true,
          },
        ]}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(ToppedUp);
